'use client';

import React, { useState, useRef, useEffect } from 'react';
import { cn } from '@/utils';
import { Icon } from '@/components';

export interface ISelectOption {
  /**
   * The value of the option
   */
  value: string;
  /**
   * The display label for the option
   */
  label: string;
  /**
   * Whether the option is disabled
   */
  disabled?: boolean;
}

export interface SelectProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /**
   * Array of options for the select dropdown
   */
  options: ISelectOption[];

  /**
   * Currently selected value
   */
  value?: string;

  /**
   * Label for the select field
   */
  label?: string;

  /**
   * Placeholder text when no option is selected
   */
  placeholder?: string;

  /**
   * Whether the select is disabled
   */
  disabled?: boolean;

  /**
   * Error message to display, or true for error state
   */
  error?: string | boolean;

  /**
   * Whether the select is in a loading state
   */
  isLoading?: boolean;

  /**
   * Callback when selection changes
   */
  onChange?: (value: string) => void;

  /**
   * Callback when dropdown opens/closes
   */
  onOpenChange?: (isOpen: boolean) => void;

  /**
   * Unique identifier for the select
   */
  id?: string;

  /**
   * Name attribute for form integration
   */
  name?: string;

  /**
   * ARIA label for accessibility
   */
  'aria-label'?: string;

  /**
   * Whether to show the selected option's label as the value in the select trigger.
   * If false, the trigger will always show the placeholder text, even when an option
   * is selected. This can be useful in cases where you want to indicate that a
   * selection has been made without showing the specific option in the trigger.
   *
   * Defaults to true.
   */
  showSelectionAsValue?: boolean;
}

/**
 * Select Component
 *
 * A dropdown select component that matches the Figma design with proper
 * accessibility features and keyboard navigation.
 */
export const Select: React.FC<SelectProps> = ({
  options = [],
  value,
  label,
  placeholder = 'Select an option',
  disabled = false,
  error = false,
  isLoading = false,
  onChange,
  onOpenChange,
  className,
  id,
  name,
  'aria-label': ariaLabel,
  showSelectionAsValue = true,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const selectRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);
  const optionRefs = useRef<(HTMLLIElement | null)[]>([]);

  // Generate unique ID for listbox
  const listboxId = `${id || 'select'}-listbox`;

  // Find selected option
  const selectedOption = options.find((option) => option.value === value);

  // Filter out disabled options for keyboard navigation
  const enabledOptions = options.filter((option) => !option.disabled);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (onOpenChange) {
      onOpenChange(isOpen);
    }
  }, [isOpen, onOpenChange]);

  const handleToggleOpen = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      setFocusedIndex(-1);
    }
  };

  const handleSelectOption = (option: ISelectOption) => {
    if (!option.disabled) {
      onChange?.(option.value);
      setIsOpen(false);
      setFocusedIndex(-1);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else if (focusedIndex >= 0 && focusedIndex < enabledOptions.length) {
          const optionIndex = options.findIndex(
            (opt) => opt.value === enabledOptions[focusedIndex].value,
          );
          handleSelectOption(options[optionIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setFocusedIndex((prev) => (prev < enabledOptions.length - 1 ? prev + 1 : 0));
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (isOpen) {
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : enabledOptions.length - 1));
        }
        break;
    }
  };

  // Scroll focused option into view
  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && listboxRef.current) {
      const focusedElement = optionRefs.current[focusedIndex];
      if (focusedElement) {
        focusedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
      }
    }
  }, [focusedIndex, isOpen]);

  // Base styles matching Figma design
  const selectClasses = cn(
    'relative inline-flex items-center justify-between',
    'border border-neutral-gray-400 rounded-lg',
    'px-3 py-2 gap-3',
    'font-text text-button-small font-bold leading-button-small',
    'text-semantic-text-primary',
    'cursor-pointer select-none',
    'transition-colors duration-200',
    'min-w-40',
    'z-10',
    {
      'cursor-not-allowed': disabled,
      'border-system-error': !!error && !disabled,
      'hover:border-neutral-gray-700': !disabled && !isOpen,
    },
    className,
  );

  const dropdownClasses = cn(
    'absolute top-full left-0 right-0 mt-4 z-50',
    'bg-neutral-white',
    'border border-neutral-white rounded-lg',
    'max-h-60 overflow-auto',
    'min-w-64',
    'p-2',
    'shadow-md',
  );

  const optionClasses = (option: ISelectOption, index: number) => {
    const enabledIndex = enabledOptions.findIndex((opt) => opt.value === option.value);
    const isFocused = enabledIndex === focusedIndex;
    const isSelected = option.value === value;

    return cn(
      'border border-transparent rounded-md',
      'px-3 py-2 cursor-pointer',
      'font-text text-body leading-body text-neutral-black',
      'transition-colors duration-150',
      'truncate w-full',
      {
        'text-semantic-text-primary': !option.disabled,
        'text-neutral-gray-400': option.disabled,
        'cursor-not-allowed': option.disabled,
        'bg-neutral-gray-100': isSelected && !option.disabled,
        'hover:text-brand-primary': !option.disabled && !isFocused && !isSelected,
      },
    );
  };

  return (
    <div className="space-y-1">
      {label && (
        <label
          htmlFor={id}
          className="block font-text text-body font-medium leading-body text-semantic-text-primary"
        >
          {label}
        </label>
      )}

      <div
        ref={selectRef}
        className={selectClasses}
        onClick={handleToggleOpen}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-label={ariaLabel}
        id={id}
        {...props}
      >
        {/* Hidden input for form integration */}
        {name && <input type="hidden" name={name} value={value || ''} />}

        {/* Selected value or placeholder */}
        <span
          className={cn('pointer-events-none truncate', {
            'text-neutral-gray-500': !selectedOption,
            'text-neutral-black': showSelectionAsValue && selectedOption,
          })}
        >
          {showSelectionAsValue
            ? isLoading
              ? 'Loading...'
              : selectedOption?.label || placeholder
            : placeholder}
        </span>

        {/* Dropdown icon */}
        <Icon
          name="CaretDown"
          size={16}
          className={cn('flex-shrink-0 transition-transform duration-200', {
            'rotate-180': isOpen,
          })}
        />

        {/* Dropdown menu */}
        {isOpen && !disabled && (
          <ul
            ref={listboxRef}
            id={listboxId}
            className={dropdownClasses}
            role="listbox"
            aria-label="Options"
          >
            {options.map((option, index) => (
              <li
                key={option.value}
                className={optionClasses(option, index)}
                role="option"
                aria-selected={option.value === value}
                aria-disabled={option.disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectOption(option);
                }}
                ref={(el) => {
                  optionRefs.current[index] = el;
                }}
              >
                <span className="pointer-events-none">{option.label}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Error message */}
      {typeof error === 'string' && (
        <p className="font-text text-sm leading-body text-system-error">{error}</p>
      )}
    </div>
  );
};

Select.displayName = 'Select';

export default Select;
